import React from "react";
import { Select, Button, DatePicker, Input } from "antd";
import { DeleteOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import type { ColumnsType } from "antd/es/table";
import { DataTable } from "../../../../../components/shared";

import { TestCategory, TestType } from "../../../../../types/healthcare/TestTypes";
import { LabOrder, LabOrderItem, LabOrderItemStatusOptions, parseTestType } from "../../../../../types/lab/LabOrderTypes";
import { sampleTypeOptions } from "../../../../../types/lab/SampleTypes";

import { useNotifications } from "../../../../../context/NotificationContext";

const { TextArea } = Input;

interface OrderItemsProps {
	orderData: Partial<LabOrder>;
	setOrderData: React.Dispatch<React.SetStateAction<Partial<LabOrder>>>;
	testCategories: TestCategory[];
	testTypes: TestType[];
	lab_order_id: number;
	onCategoryChange: (categoryValue: string) => void;
	autoSave: () => Promise<void>;
}

interface OptionType {
	label: string;
	value: string;
}

const OrderItems: React.FC<OrderItemsProps> = ({ orderData, setOrderData, testCategories, testTypes, lab_order_id, onCategoryChange, autoSave }) => {
	const order_items = orderData.order_items || [];
	const { showWarning, showError, showSuccess, showInfo } = useNotifications();

	const testCategoryOptions: OptionType[] = testCategories.map((category) => ({
		label: category.name,
		value: category.value,
	}));

	const testTypeOptions: OptionType[] = testTypes.map((testType) => ({
		label: testType.name,
		value: testType.value,
	}));

	const isDisabled = orderData.status === "completed";

	const updateOrderItem = (itemId: number, field: string, value: any) => {
		setOrderData((prev) => {
			const updatedItems = [...(prev.order_items || [])];
			const itemIndex = updatedItems.findIndex((item) => item.id === itemId);

			if (itemIndex !== -1) {
				// Handle test_type specially - store as JSON string
				if (field === "test_type" && Array.isArray(value)) {
					updatedItems[itemIndex] = {
						...updatedItems[itemIndex],
						[field]: JSON.stringify(value),
					};
				} else {
					updatedItems[itemIndex] = {
						...updatedItems[itemIndex],
						[field]: value,
					};
				}

				if (field === "test_category") {
					updatedItems[itemIndex].test_type = JSON.stringify([]);
					onCategoryChange(value);
				}
			}

			return { ...prev, order_items: updatedItems };
		});

		autoSave();
	};

	const handleRemoveOrderItem = (itemId: number) => {
		setOrderData((prev) => {
			const updatedItems = (prev.order_items || []).filter((item) => item.id !== itemId);
			return { ...prev, order_items: updatedItems };
		});

		showInfo("Order item removed");
		autoSave();
	};

	const handleBulkStatusChange = (value: string) => {
		if (!order_items || order_items.length === 0) return;

		order_items.forEach((item) => {
			updateOrderItem(item.id, "status", value);
		});

		showInfo(`All item statuses set to ${value}`);
	};

	const columns: ColumnsType<LabOrderItem> = [
		{
			title: "Sample ID",
			dataIndex: "sample_id",
			key: "sample_id",
			width: 120,
			render: (text: string, record: LabOrderItem) => (
				<TextArea value={text || ""} onChange={(e) => updateOrderItem(record.id, "sample_id", e.target.value)} disabled={isDisabled} placeholder="Sample ID" autoSize={{ minRows: 1, maxRows: 3 }} size="small" />
			),
		},
		{
			title: "Test Category",
			dataIndex: "test_category",
			key: "test_category",
			width: 150,
			render: (text: string, record: LabOrderItem) => (
				<Select
					value={text || null}
					options={testCategoryOptions}
					onChange={(value) => updateOrderItem(record.id, "test_category", value)}
					placeholder="Select Test Category"
					disabled={isDisabled}
					showSearch
					filterOption={(input, option) => (option?.label ?? "").toLowerCase().includes(input.toLowerCase())}
					size="small"
					style={{ width: "100%" }}
				/>
			),
		},
		{
			title: "Test Types",
			dataIndex: "test_type",
			key: "test_type",
			width: 250,
			render: (text: string, record: LabOrderItem) => {
				const currentTestTypes = parseTestType(text);
				return (
					<Select
						mode="multiple"
						value={currentTestTypes}
						options={testTypeOptions}
						onChange={(value) => {
							console.log("OrderItems - Selected test types:", value); // Debug log
							updateOrderItem(record.id, "test_type", value);
						}}
						placeholder="Select Test Types"
						disabled={isDisabled || !record.test_category}
						showSearch
						filterOption={(input, option) => (option?.label ?? "").toLowerCase().includes(input.toLowerCase())}
						notFoundContent="Select a category first"
						size="small"
						style={{ width: "100%" }}
						maxTagCount="responsive"
					/>
				);
			},
		},
		{
			title: "Sample Type",
			dataIndex: "sample_type",
			key: "sample_type",
			width: 150,
			render: (text: string, record: LabOrderItem) => (
				<Select value={text || null} options={sampleTypeOptions} onChange={(value) => updateOrderItem(record.id, "sample_type", value)} placeholder="Select Sample Type" disabled={isDisabled} size="small" style={{ width: "100%" }} />
			),
		},
		{
			title: (
				<div style={{ display: "flex", alignItems: "center", gap: 8 }}>
					<span>Status</span>
					{!isDisabled && <Select options={LabOrderItemStatusOptions} onChange={handleBulkStatusChange} placeholder="Bulk Update" size="small" style={{ width: 120 }} />}
				</div>
			),
			dataIndex: "status",
			key: "status",
			width: 150,
			render: (text: string, record: LabOrderItem) => (
				<Select value={text || null} options={LabOrderItemStatusOptions} onChange={(value) => updateOrderItem(record.id, "status", value)} placeholder="Select Status" disabled={isDisabled} size="small" style={{ width: "100%" }} />
			),
		},
		...(orderData.shipping_status !== "not_shipped" && orderData.shipping_status !== undefined
			? [
					{
						title: "Received At",
						dataIndex: "received_at",
						key: "received_at",
						width: 200,
						render: (text: string, record: LabOrderItem) => (
							<DatePicker
								value={text ? dayjs(text) : null}
								onChange={(date) => updateOrderItem(record.id, "received_at", date?.toISOString())}
								showTime={{ format: "HH:mm" }}
								format="DD/MM/YYYY HH:mm"
								disabled={isDisabled}
								placeholder="Received date/time"
								size="small"
								style={{ width: "100%" }}
							/>
						),
					},
			  ]
			: []),
		{
			title: "Notes",
			dataIndex: "notes",
			key: "notes",
			width: 200,
			render: (text: string, record: LabOrderItem) => (
				<TextArea value={text || ""} onChange={(e) => updateOrderItem(record.id, "notes", e.target.value)} disabled={isDisabled} placeholder="Notes" autoSize={{ minRows: 1, maxRows: 3 }} size="small" />
			),
		},
		...(!isDisabled
			? [
					{
						title: "Actions",
						key: "actions",
						width: 80,
						fixed: "right" as const,
						render: (_: any, record: LabOrderItem) => <Button type="text" danger icon={<DeleteOutlined />} onClick={() => handleRemoveOrderItem(record.id)} size="small" />,
					},
			  ]
			: []),
	];

	return (
		<DataTable<LabOrderItem>
			columns={columns}
			dataSource={order_items}
			rowKey="id"
			pagination={false}
			scrollX="max-content"
			scrollY="calc(100vh - 225px)"
			bare
			emptyTitle="No order items added yet"
			emptyDescription='Click "Add Order Item" in the drawer footer to get started'
		/>
	);
};

export default OrderItems;
